// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the tidy Assistant chat (design/assistant-dm-cleanup). Holds the server's chat state
// (conversation index, archived, pinned, muted, live cards) and what the person did on this device:
// the view picked in the list drawer, the stacks and conversations they opened or folded, the
// search, the open History. The layout itself is computed in utils/AssistantChatLayout.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {assistantChannelIdNow} from '@app/features/konvo_assistant/state/AssistantUnread';
import type {AssistantChatView} from '@app/features/konvo_assistant/utils/AssistantChatLayout';
import {ChannelMessages} from '@app/features/messaging/state/ChannelMessages';
import {http} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {makePersistent} from '@app/features/platform/utils/MobXPersistence';
import type {
	AssistantChatHistoryResponse,
	AssistantChatKind,
	AssistantChatState,
} from '@konvo/schema/src/domains/konvo/AssistantChat';
import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('AssistantChat');
/** Refetch at most this often when Konvo posts (the index follows each turn). */
const REFRESH_DEBOUNCE_MS = 1500;

export type AssistantSearchFilter = 'everything' | 'chats' | 'updates' | 'drafts';

export interface AssistantHistoryTarget {
	messageId: string;
	kind: AssistantChatKind;
}

class AssistantChat {
	state: AssistantChatState | null = null;
	loading = false;
	view: AssistantChatView = {type: 'all'};
	/** The newest message when the view was picked (see AssistantChatLayoutInput.viewSinceId). */
	viewSinceId: string | null = null;
	expandedStacks = new Set<string>();
	toggledConversations = new Set<string>();
	drawerOpen = false;
	/** Kept open as a side column (desktop), remembered on this device. */
	drawerPinned = false;
	/** The newest message when New chat was last pressed, remembered on this device: older conversations fold. */
	newChatAfterId: string | null = null;
	searchQuery = '';
	searchOpen = false;
	searchFilter: AssistantSearchFilter = 'everything';
	searchArchived = true;
	history: AssistantHistoryTarget | null = null;
	private refreshTimer: ReturnType<typeof setTimeout> | null = null;
	private reloadQueued = false;
	/** Bumped on every change to `state`, so a load that started earlier does not overwrite it. */
	private stateVersion = 0;

	constructor() {
		makeAutoObservable<this, 'refreshTimer' | 'reloadQueued' | 'stateVersion'>(
			this,
			{refreshTimer: false, reloadQueued: false, stateVersion: false},
			{autoBind: true},
		);
		void makePersistent(this, 'AssistantChatPrefs', ['drawerPinned', 'newChatAfterId'], {syncAcrossTabs: true});
	}

	get archivedIds(): ReadonlySet<string> {
		return new Set(this.state?.archived_message_ids ?? []);
	}

	get pinnedIds(): ReadonlyArray<string> {
		return this.state?.pinned_message_ids ?? [];
	}

	isMuted(kind: AssistantChatKind | null): boolean {
		return kind !== null && (this.state?.muted_kinds ?? []).includes(kind);
	}

	async load(): Promise<void> {
		if (this.loading) {
			// Something changed while a load was on its way: load again once it lands.
			this.reloadQueued = true;
			return;
		}
		this.loading = true;
		const version = this.stateVersion;
		try {
			const response = await http.get<AssistantChatState>(Endpoints.KONVO_ASSISTANT_CHAT, {mode: 'silent'});
			runInAction(() => {
				// A change made meanwhile (archive, pin, mute) wins over what this load read before it.
				if (response.body && version === this.stateVersion) this.applyState(response.body);
			});
		} catch (error) {
			logger.warn('Could not load the Assistant chat state', error);
		} finally {
			runInAction(() => {
				this.loading = false;
			});
			if (this.reloadQueued) {
				this.reloadQueued = false;
				void this.load();
			}
		}
	}

	private applyState(state: AssistantChatState | null): void {
		this.state = state;
		this.stateVersion++;
	}

	/** Called when Konvo or the person posts in the Assistant chat. */
	scheduleRefresh(): void {
		if (this.refreshTimer) clearTimeout(this.refreshTimer);
		this.refreshTimer = setTimeout(() => {
			this.refreshTimer = null;
			void this.load();
		}, REFRESH_DEBOUNCE_MS);
	}

	setView(view: AssistantChatView): void {
		this.view = view;
		const channelId = assistantChannelIdNow();
		this.viewSinceId = channelId ? (ChannelMessages.get(channelId)?.last()?.id ?? null) : null;
		if (!this.drawerPinned) this.drawerOpen = false;
	}

	toggleStack(key: string): void {
		const next = new Set(this.expandedStacks);
		if (next.has(key)) next.delete(key);
		else next.add(key);
		this.expandedStacks = next;
	}

	toggleConversation(id: string): void {
		const next = new Set(this.toggledConversations);
		if (next.has(id)) next.delete(id);
		else next.add(id);
		this.toggledConversations = next;
	}

	setDrawerOpen(open: boolean): void {
		this.drawerOpen = open;
	}

	toggleDrawer(): void {
		this.drawerOpen = !this.drawerOpen;
	}

	setDrawerPinned(pinned: boolean): void {
		this.drawerPinned = pinned;
		this.drawerOpen = pinned;
	}

	setSearchQuery(query: string): void {
		this.searchQuery = query;
		this.searchOpen = query.trim().length > 0;
	}

	closeSearch(): void {
		this.searchQuery = '';
		this.searchOpen = false;
	}

	setSearchFilter(filter: AssistantSearchFilter): void {
		this.searchFilter = filter;
	}

	setSearchArchived(include: boolean): void {
		this.searchArchived = include;
	}

	openHistory(target: AssistantHistoryTarget | null): void {
		this.history = target;
	}

	/** Starts a fresh conversation: Konvo forgets the current one; it folds away in the chat. */
	async startNewChat(): Promise<void> {
		const channelId = assistantChannelIdNow();
		const lastMessageId = channelId ? (ChannelMessages.get(channelId)?.last()?.id ?? null) : null;
		this.view = {type: 'all'};
		this.toggledConversations = new Set();
		if (lastMessageId) this.newChatAfterId = lastMessageId;
		await http.post(Endpoints.KONVO_ASSISTANT_CHAT_NEW, {body: {}});
		await this.load();
	}

	async setArchived(input: {messageIds?: Array<string>; conversationIds?: Array<string>}, archived: boolean) {
		const previous = this.state;
		if (previous) {
			const ids = new Set(previous.archived_message_ids);
			for (const id of input.messageIds ?? []) {
				if (archived) ids.add(id);
				else ids.delete(id);
			}
			this.stateVersion++;
			this.state = {
				...previous,
				archived_message_ids: [...ids],
				conversations: previous.conversations.map((conversation) =>
					input.conversationIds?.includes(conversation.id) ? {...conversation, archived} : conversation,
				),
			};
		}
		try {
			const response = await http.put<AssistantChatState>(Endpoints.KONVO_ASSISTANT_CHAT_ARCHIVE, {
				body: {message_ids: input.messageIds ?? [], conversation_ids: input.conversationIds ?? [], archived},
			});
			runInAction(() => {
				if (response.body) this.applyState(response.body);
			});
		} catch (error) {
			runInAction(() => {
				if (previous) this.applyState(previous);
			});
			throw error;
		}
	}

	async setPinned(messageId: string, pinned: boolean): Promise<void> {
		const response = await http.put<AssistantChatState>(Endpoints.KONVO_ASSISTANT_CHAT_PIN, {
			body: {message_id: messageId, pinned},
		});
		runInAction(() => {
			if (response.body) this.applyState(response.body);
		});
	}

	async setMuted(kind: AssistantChatKind, muted: boolean): Promise<void> {
		const response = await http.put<AssistantChatState>(Endpoints.KONVO_ASSISTANT_CHAT_MUTE, {body: {kind, muted}});
		runInAction(() => {
			if (response.body) this.applyState(response.body);
		});
	}

	async renameConversation(id: string, title: string): Promise<void> {
		await http.patch(Endpoints.KONVO_ASSISTANT_CHAT_CONVERSATION(id), {body: {title}});
		await this.load();
	}

	async fetchHistory(kind: AssistantChatKind): Promise<AssistantChatHistoryResponse | null> {
		try {
			const response = await http.get<AssistantChatHistoryResponse>(Endpoints.KONVO_ASSISTANT_CHAT_HISTORY, {
				query: {kind},
				mode: 'silent',
			});
			return response.body ?? null;
		} catch (error) {
			logger.warn('Could not load card history', error);
			return null;
		}
	}
}

export default new AssistantChat();
